import { useEffect, useState, useMemo } from 'react';
import { TriangleAlert } from 'lucide-react';
import { useModal } from '@/hooks';
import {
	ServiceModalityFeedback,
	SessionStepTitle,
	TimeSlotSelect,
	AlertContainer,
	AvailableDatePicker,
	Button,
} from '@/components';
import { useGetAvailableDate, useGetClinic } from '@/services';

interface ReturnSessionModalProps {
	professional: Professionals.Professional;
	setSelectedReturnDate: React.Dispatch<
		React.SetStateAction<{
			available_date_id: string;
			is_online: boolean;
			selected_date: string;
		}>
	>;
}

export const ReturnSessionModal = ({
	professional,
	setSelectedReturnDate,
}: ReturnSessionModalProps) => {
	const { closeModal } = useModal();

	const [isCancel, setIsCancel] = useState(false);

	const [availableSlots, setAvailableSlots] = useState<
		AvailableDates.SessionTimeSlot[]
	>([]);
	const [selectedDate, setSelectedDate] = useState('');

	const { data: availabilityData, isLoading } = useGetAvailableDate(
		professional.id,
	);
	const { data: clinicData } = useGetClinic(professional.clinic_id || ''); // Segurança adicionada

	const allHours = useMemo(() => {
		const allAvailableHours =
			availabilityData?.professionalAvailability.availability.flatMap(
				(day) => day.hours,
			) || [];
		return allAvailableHours;
	}, [availabilityData]);

	const localAvailableDates = useMemo(() => {
		const localDates = allHours.map((hour) => {
			const dateObj = new Date(hour.start_time);
			const year = dateObj.getFullYear();
			const month = String(dateObj.getMonth() + 1).padStart(2, '0');
			const day = String(dateObj.getDate()).padStart(2, '0');
			return `${year}-${month}-${day}`;
		});
		return Array.from(new Set(localDates));
	}, [allHours]);

	const getSlotsForLocalDate = (date: string) => {
		const [day, month, year] = date.split('/');
		const formattedDate = `${year}-${month}-${day}`;
		return allHours.filter((hour) => {
			const dateObj = new Date(hour.start_time);
			const localDateString = `${dateObj.getFullYear()}-${String(
				dateObj.getMonth() + 1,
			).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;
			return localDateString === formattedDate;
		});
	};

	const handleDateSelect = (date: string | null) => {
		if (!date) return;
		const slots = getSlotsForLocalDate(date);
		setAvailableSlots(slots);
		setSelectedDate(date);
	};

	const handleTimeSelect = (timeSlotJson: string) => {
		const parsedSlot = JSON.parse(timeSlotJson);
		setSelectedReturnDate((prev) => ({
			...prev,
			available_date_id: parsedSlot.available_date_id,
			selected_date: parsedSlot.start_time,
		}));
	};

	const handleOnModalityChange = (
		modality: Professionals.ServiceModality,
	) => {
		setSelectedReturnDate((prev) => ({
			...prev,
			is_online: modality === 'FULL_REMOTE' || modality === 'HYBRID',
		}));
	};

	// Ajuste: Inicializa a modalidade com base na do profissional
	useEffect(() => {
		const professionalServiceModality = professional.service_modality;
		const isOnline =
			professionalServiceModality === 'HYBRID' ||
			professionalServiceModality === 'FULL_REMOTE';
		setSelectedReturnDate((prev) => ({
			...prev,
			is_online: isOnline,
		}));
	}, [professional.service_modality, setSelectedReturnDate]); // Adicionada setSelectedReturnDate

	const handleConfirmCancel = () => {
		closeModal();
		const event = new CustomEvent('openCancelInfoModal');
		window.dispatchEvent(event);
	};

	return (
		<div className="flex flex-col gap-6 md:flex-row">
			<div className="space-y-2">
				<SessionStepTitle
					title="Selecione uma data"
					className="!text-base font-normal text-dark"
				/>
				<AvailableDatePicker
					availableDates={localAvailableDates}
					onDateChange={handleDateSelect}
					loading={isLoading}
				/>
			</div>
			<div className="space-y-4">
				<div className="flex flex-col space-y-2">
					<SessionStepTitle
						title="Selecione um horário"
						className="!text-base font-normal text-dark"
					/>
					<TimeSlotSelect
						slots={availableSlots}
						onValueChange={handleTimeSelect}
						disabled={!selectedDate}
					/>
				</div>

				<ServiceModalityFeedback
					data={professional}
					address={clinicData?.address}
					onModalityChange={handleOnModalityChange}
				/>
				{/* Lógica de Cancelamento (Mantida igual) */}
				{!isCancel ? (
					<Button
						onClick={() => setIsCancel(true)}
						type="button"
						text="Cancelar consulta"
						className="mt-4 w-full rounded-lg border border-solid border-stone-200 bg-transparent py-2 text-sm font-bold text-stone-600 transition-all hover:border-stone-300 hover:bg-stone-50"
					/>
				) : (
					<AlertContainer
						message={
							<div className="space-y-2">
								<div>
									<p className="text-dark">
										Tem certeza que deseja cancelar a
										consulta?
									</p>
									<span className="text-red-600">
										Essa é uma ação irreversível!
									</span>
								</div>
								<Button
									onClick={handleConfirmCancel}
									startIcon={
										<TriangleAlert className="size-3" />
									}
									className="w-full justify-center rounded-lg bg-stone-900 py-2 text-sm font-bold text-white shadow-sm transition-colors hover:bg-stone-800"
									text="Confirmar"
								/>
							</div>
						}
					/>
				)}
			</div>
		</div>
	);
};
